
import React from "react";
import { Control } from "react-hook-form";
import { 
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import PaymentStatusField from "./PaymentStatusField";
import DateField from "./DateField";

interface ClientDetailsStepProps {
  control: Control<any>;
  onNext?: () => Promise<void>;
  onCancel: () => void;
  isEditMode?: boolean;
  isLoading?: boolean;
}

const ClientDetailsStep: React.FC<ClientDetailsStepProps> = ({ 
  control, 
  onNext, 
  onCancel,
  isEditMode = false,
  isLoading = false
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <FormField
          control={control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">{t('clients.firstName')} *</FormLabel>
              <FormControl>
                <Input 
                  placeholder={t('clients.firstName')} 
                  className="h-11 md:h-10 text-base md:text-sm"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">{t('clients.lastName')} *</FormLabel>
              <FormControl>
                <Input 
                  placeholder={t('clients.lastName')} 
                  className="h-11 md:h-10 text-base md:text-sm"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      
      <FormField
        control={control}
        name="phone"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium">{t('clients.phone')} *</FormLabel>
            <FormControl>
              <Input 
                placeholder={t('clients.phone')} 
                className="h-11 md:h-10 text-base md:text-sm"
                {...field} 
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      
      <FormField
        control={control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium">{t('clients.email')}</FormLabel>
            <FormControl>
              <Input 
                type="email" 
                placeholder={t('clients.email')} 
                className="h-11 md:h-10 text-base md:text-sm"
                {...field}
                value={field.value || ""}
                onChange={(e) => field.onChange(e.target.value || "")}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <DateField
          control={control}
          name="birthDate"
          label={t('clients.birthDate')}
          placeholder={t('clients.selectBirthDate')}
        />
        
        <FormField
          control={control}
          name="gender"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">{t('clients.gender')}</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="h-11 md:h-10 text-base md:text-sm">
                    <SelectValue placeholder={t('clients.selectGender')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="male">{t('clients.male')}</SelectItem>
                  <SelectItem value="female">{t('clients.female')}</SelectItem>
                  <SelectItem value="other">{t('clients.other')}</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <FormField
          control={control}
          name="weightKg"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">{t('clients.weight')}</FormLabel>
              <FormControl>
                <Input 
                  type="number"
                  step="0.1"
                  min="1"
                  max="999"
                  placeholder={t('clients.weightPlaceholder')} 
                  className="h-11 md:h-10 text-base md:text-sm"
                  {...field}
                  value={field.value?.toString() || ""}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={control}
          name="heightCm"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">{t('clients.height')}</FormLabel>
              <FormControl>
                <Input 
                  type="number"
                  step="0.1"
                  min="1"
                  max="999"
                  placeholder={t('clients.heightPlaceholder')} 
                  className="h-11 md:h-10 text-base md:text-sm"
                  {...field}
                  value={field.value?.toString() || ""}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      
      <FormField
        control={control}
        name="address"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium">{t('clients.address')}</FormLabel>
            <FormControl>
              <Input 
                placeholder={t('clients.address')} 
                className="h-11 md:h-10 text-base md:text-sm"
                {...field} 
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Payment Status Field - Only show in edit mode */}
      {isEditMode && (
        <PaymentStatusField control={control} />
      )}

      <FormField
        control={control}
        name="notes"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-sm font-medium">{t('clients.notes')}</FormLabel>
            <FormControl>
              <Textarea 
                placeholder={t('clients.notesPlaceholder')} 
                className="min-h-[100px] md:min-h-[80px] text-base md:text-sm resize-none"
                {...field} 
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      
      <div className="flex flex-col md:flex-row justify-end gap-3 md:gap-2 pt-4 md:pt-2">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel}
          className="h-11 md:h-10 text-base md:text-sm order-2 md:order-1"
        >
          {t('common.cancel')}
        </Button>
        {isEditMode ? (
          <Button 
            type="submit" 
            disabled={isLoading}
            className="h-11 md:h-10 text-base md:text-sm order-1 md:order-2"
          >
            {isLoading ? t('clients.updating') : t('clients.updateDetails')}
          </Button>
        ) : (
          onNext && (
            <Button 
              type="button" 
              onClick={onNext}
              className="h-11 md:h-10 text-base md:text-sm order-1 md:order-2"
            >
              {t('clients.nextSubscriptions')}
            </Button>
          )
        )}
      </div>
    </div>
  );
};

export default ClientDetailsStep;
